import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useParams, withRouter } from 'react-router-dom';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'utils/dayjs';

import {
  fetchAllCurrencyList,
  fetchBreadcrumbs,
  fetchCurrencyYearArchive,
  fetchSeoMeta,
  fetchSeoTextsBlocks,
} from 'actions';
import { useOnClickOutside, useSelectorProps } from 'hooks';
import { usePageSchema } from 'hooks/usePageSchema';
import { getCurrentCurrency } from 'selectors';
import {
  getCurrencyYearArchiveDefaultDate,
  getCurrencyYearArchivePath,
  getCurrencyYearArchiveSourceSectionId,
  getCurrencyYearArchiveUpdatedDate,
  isCurrencyYearArchiveForRoute,
  isCurrentArchiveYear,
  parseCurrencyYearArchiveHighlightDate,
  scrollToCurrencyYearArchiveSourceSection,
} from 'utils';

import Page from 'components/Page';
import PageHelmet from 'components/PageHelmet';
import PageInner from 'components/PageInner';
import PageSidebar from 'components/PageSidebar';
import LayoutContainer from 'components/LayoutContainer';
import CurrencyYearArchiveChart from 'components/CurrencyYearArchiveChart';
import CurrencyYearArchiveConverter from 'components/CurrencyYearArchiveConverter';
import CurrencyYearArchiveCurrencyTabs from 'components/CurrencyYearArchiveCurrencyTabs';
import CurrencyYearArchivePageContent from 'components/CurrencyYearArchivePageContent';
import CurrencyYearArchivePageFooter from 'components/CurrencyYearArchivePageFooter';
import DatePicker from 'components/commons/DatePicker';
import Icon from 'components/commons/Icon';
import Select from 'components/commons/Select';
import Updated from 'components/commons/Updated';
import {
  CURRENCY_YEAR_ARCHIVE_DEFAULT_SOURCE,
  CURRENCY_YEAR_ARCHIVE_SOURCE_FILTER_OPTIONS,
} from 'constants/currencyYearArchive';

import {
  Content,
  ControlsMain,
  ControlsRow,
  CurrencyTabsSlot,
  DateControls,
  DateSelectWrapper,
  HeaderBlock,
  HeaderSection,
  IntroBlock,
  SelectButton,
  SelectValue,
  SourceFilterSlot,
  Title,
  TitleRow,
  UpdatedSlot,
  Wrapper,
  YearSelectList,
  YearSelectOption,
  YearSelectWrapper,
} from './styles';

const SOURCE_FILTER_OPTIONS = CURRENCY_YEAR_ARCHIVE_SOURCE_FILTER_OPTIONS.map(
  source => ({
    value: source,
    label: source,
  })
);

const getSourceFilterMessageId = item =>
  `currencyYearArchivePage.sourceFilter.${item}`;

const CurrencyYearArchivePage = memo(({ location, staticContext }) => {
  const { formatMessage, locale } = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();
  const selectorProps = useSelectorProps();
  const { currency, year } = useParams();
  const yearSelectRef = useRef(null);
  const yearSelectButtonRef = useRef(null);
  const [isYearSelectOpen, setIsYearSelectOpen] = useState(false);
  const [yearSelectDropdownRect, setYearSelectDropdownRect] = useState(null);
  const [pickerMonthValue, setPickerMonthValue] = useState(null);
  const [isArchiveFetchSettled, setIsArchiveFetchSettled] = useState(
    Boolean(staticContext)
  );
  const [selectedChartSource, setSelectedChartSource] = useState(
    CURRENCY_YEAR_ARCHIVE_DEFAULT_SOURCE
  );
  const [activeSourceTab, setActiveSourceTab] = useState(
    CURRENCY_YEAR_ARCHIVE_DEFAULT_SOURCE
  );
  const shouldScrollToHighlightedDateRef = useRef(false);
  const [highlightHash, setHighlightHash] = useState('');

  const onSourceTabSelect = useCallback(source => {
    setActiveSourceTab(source);
    scrollToCurrencyYearArchiveSourceSection(source);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const source = [...CURRENCY_YEAR_ARCHIVE_SOURCE_FILTER_OPTIONS]
        .reverse()
        .find(option => {
          const element = document.getElementById(
            getCurrencyYearArchiveSourceSectionId(option)
          );

          return element && element.getBoundingClientRect().top <= 64;
        });

      if (source) {
        setActiveSourceTab(current => (current === source ? current : source));
      }
    };

    window.addEventListener('scroll', onScroll, true);

    return () => window.removeEventListener('scroll', onScroll, true);
  }, []);

  const updateYearSelectDropdownRect = useCallback(() => {
    const button = yearSelectButtonRef.current;

    if (!button) {
      return;
    }

    const { bottom, left, width } = button.getBoundingClientRect();

    setYearSelectDropdownRect({
      top: bottom + 4,
      left,
      width,
    });
  }, []);

  const currencyYearArchive = useSelector(
    state => state.currency?.currencyYearArchive
  );

  const archiveForRoute = useMemo(() => {
    if (!isCurrencyYearArchiveForRoute(currencyYearArchive, currency, year)) {
      return null;
    }

    return currencyYearArchive;
  }, [currencyYearArchive, currency, year]);

  const displayedArchiveRef = useRef(archiveForRoute);

  if (archiveForRoute) {
    displayedArchiveRef.current = archiveForRoute;
  }

  const displayedArchive = archiveForRoute || displayedArchiveRef.current;

  const currentCurrency = useSelector(state =>
    getCurrentCurrency(state, { currency })
  );

  const currencyLabel =
    currentCurrency?.nameG || currentCurrency?.name || currency?.toUpperCase();

  const hashHighlightedDate = useMemo(
    () => parseCurrencyYearArchiveHighlightDate(highlightHash, year),
    [highlightHash, year]
  );

  const archiveYear = displayedArchive ? displayedArchive.year : year;

  const defaultArchiveDate = useMemo(
    () =>
      getCurrencyYearArchiveDefaultDate({
        year: archiveYear,
        months: displayedArchive?.sources?.cash?.months,
        highlightedDate: hashHighlightedDate,
      }),
    [archiveYear, displayedArchive, hashHighlightedDate]
  );

  const highlightedDate = defaultArchiveDate?.isValid()
    ? defaultArchiveDate.format('YYYY-MM-DD')
    : null;

  useLayoutEffect(() => {
    setHighlightHash(location.hash || window.location.hash);
  }, [location.hash]);

  useEffect(() => {
    if (hashHighlightedDate) {
      return;
    }

    window.scrollTo(0, 0);
  }, [location.pathname, hashHighlightedDate]);

  useEffect(() => {
    if (
      !highlightedDate ||
      !archiveForRoute ||
      !shouldScrollToHighlightedDateRef.current
    ) {
      return undefined;
    }

    shouldScrollToHighlightedDateRef.current = false;

    let attempts = 0;
    let timeoutId;

    const scrollToHighlightedRow = () => {
      const row = document.querySelector(`[data-date="${highlightedDate}"]`);

      if (row) {
        row.scrollIntoView({ block: 'center' });
        return;
      }

      attempts += 1;

      if (attempts < 10) {
        timeoutId = window.setTimeout(scrollToHighlightedRow, 50);
      }
    };

    timeoutId = window.setTimeout(scrollToHighlightedRow, 0);

    return () => window.clearTimeout(timeoutId);
  }, [highlightedDate, archiveForRoute]);

  useEffect(() => {
    setIsArchiveFetchSettled(Boolean(staticContext));

    dispatch(fetchCurrencyYearArchive(selectorProps)).finally(() => {
      setIsArchiveFetchSettled(true);
    });
    dispatch(fetchSeoMeta(selectorProps));
    dispatch(fetchSeoTextsBlocks(selectorProps));
    dispatch(fetchBreadcrumbs(selectorProps));
    dispatch(
      fetchAllCurrencyList({
        locale,
        type: 'money',
      })
    );
  }, [
    dispatch,
    locale,
    location.pathname,
    currency,
    year,
    selectorProps,
    staticContext,
  ]);

  useLayoutEffect(() => {
    if (!isYearSelectOpen) {
      setYearSelectDropdownRect(null);
      return undefined;
    }

    updateYearSelectDropdownRect();

    window.addEventListener('resize', updateYearSelectDropdownRect);
    window.addEventListener('scroll', updateYearSelectDropdownRect, true);

    return () => {
      window.removeEventListener('resize', updateYearSelectDropdownRect);
      window.removeEventListener('scroll', updateYearSelectDropdownRect, true);
    };
  }, [isYearSelectOpen, updateYearSelectDropdownRect]);

  useOnClickOutside(yearSelectRef, () => {
    if (isYearSelectOpen) {
      setIsYearSelectOpen(false);
    }
  });

  const pageTitle = useMemo(
    () =>
      formatMessage(
        { id: 'currencyYearArchivePage.title' },
        {
          currency: currencyLabel,
          year,
        }
      ),
    [formatMessage, currencyLabel, year]
  );

  const defaultMetaTitle = useMemo(
    () =>
      formatMessage(
        { id: 'currencyYearArchivePage.metaTitle' },
        {
          currency: currencyLabel,
          year,
        }
      ),
    [formatMessage, currencyLabel, year]
  );

  const defaultMetaDescription = useMemo(
    () =>
      formatMessage(
        { id: 'currencyYearArchivePage.metaDescription' },
        {
          currency: currencyLabel,
          year,
        }
      ),
    [formatMessage, currencyLabel, year]
  );

  const {
    pageScripts,
    formattedMetaTitle,
    formattedMetaDescription,
  } = usePageSchema({
    location,
    defaultMetaTitle,
    defaultMetaDescription,
  });

  const yearOptions = useMemo(
    () => (displayedArchive?.availableYears || []).map(String),
    [displayedArchive?.availableYears]
  );

  const selectedArchiveMonth = useMemo(() => {
    if (
      pickerMonthValue &&
      !(
        isCurrentArchiveYear(archiveYear) &&
        pickerMonthValue.isAfter(dayjs(), 'day')
      )
    ) {
      return pickerMonthValue;
    }

    return defaultArchiveDate;
  }, [pickerMonthValue, defaultArchiveDate, archiveYear]);

  useEffect(() => {
    setPickerMonthValue(null);
  }, [currency, year]);

  const updatedDate = useMemo(
    () => getCurrencyYearArchiveUpdatedDate(displayedArchive),
    [displayedArchive]
  );

  const onYearChange = nextYear => {
    const path = getCurrencyYearArchivePath(currency, nextYear);

    setIsYearSelectOpen(false);

    if (path) {
      history.push(path);
    }
  };

  const onMonthAccept = value => {
    if (!value) {
      return;
    }

    const selectedDate = value.format('YYYY-MM-DD');

    shouldScrollToHighlightedDateRef.current = true;

    history.push({
      pathname: location.pathname,
      search: location.search,
      hash: selectedDate,
    });
  };

  const shouldNotFound =
    (staticContext || isArchiveFetchSettled) && currencyYearArchive === null;

  const isArchiveLoading = !isArchiveFetchSettled && !displayedArchive;

  const getMonthPickerDisplayValue = (date, pickerLocale) =>
    dayjs(date, 'YYYY-MM-DD')
      .locale(pickerLocale)
      .format('D MMMM');

  if (shouldNotFound) {
    if (staticContext) {
      staticContext.status = 404;
    }

    return null;
  }

  return (
    <Page>
      <Wrapper>
        <LayoutContainer>
          <PageHelmet
            title={formattedMetaTitle}
            description={formattedMetaDescription}
            script={pageScripts}
          />
        </LayoutContainer>
      </Wrapper>
      <Content>
        <LayoutContainer>
          <PageInner>
            <CurrencyYearArchivePageContent
              sources={displayedArchive?.sources}
              year={displayedArchive ? displayedArchive.year : year}
              currencyLabel={currencyLabel}
              highlightedDate={highlightedDate}
              activeSource={activeSourceTab}
              onSourceSelect={onSourceTabSelect}
              isArchiveLoading={isArchiveLoading}
            >
              <HeaderSection>
                <IntroBlock>
                  <HeaderBlock>
                    <TitleRow>
                      <Title>{pageTitle}</Title>
                      {updatedDate ? (
                        <UpdatedSlot>
                          <Updated date={updatedDate} />
                        </UpdatedSlot>
                      ) : null}
                    </TitleRow>
                    {yearOptions.length > 0 ? (
                      <ControlsRow>
                        <ControlsMain>
                          <DateControls>
                            {displayedArchive?.sources?.cash?.months?.length >
                            0 ? (
                              <DateSelectWrapper>
                                <DatePicker
                                  type="select"
                                  views={['month', 'date']}
                                  openTo="month"
                                  disableToolbar
                                  disableFuture={isCurrentArchiveYear(
                                    archiveYear
                                  )}
                                  value={selectedArchiveMonth}
                                  onChange={setPickerMonthValue}
                                  onAccept={onMonthAccept}
                                  minDate={new Date(`${archiveYear}-01-01`)}
                                  maxDate={
                                    isCurrentArchiveYear(archiveYear)
                                      ? new Date()
                                      : new Date(`${archiveYear}-12-31`)
                                  }
                                  getDisplayValue={getMonthPickerDisplayValue}
                                />
                              </DateSelectWrapper>
                            ) : null}
                            <YearSelectWrapper ref={yearSelectRef}>
                              <SelectButton
                                ref={yearSelectButtonRef}
                                type="button"
                                $isOpen={isYearSelectOpen}
                                aria-expanded={isYearSelectOpen}
                                aria-haspopup="listbox"
                                onClick={() => {
                                  setIsYearSelectOpen(open => !open);
                                }}
                              >
                                <SelectValue>{year}</SelectValue>
                                <Icon name="arrow" />
                              </SelectButton>
                              {isYearSelectOpen && yearSelectDropdownRect ? (
                                <YearSelectList
                                  role="listbox"
                                  $fitContent
                                  $top={yearSelectDropdownRect.top}
                                  $left={yearSelectDropdownRect.left}
                                  $width={yearSelectDropdownRect.width}
                                >
                                  {yearOptions.map(availableYear => (
                                    <YearSelectOption
                                      key={availableYear}
                                      role="option"
                                      aria-selected={
                                        availableYear === String(year)
                                      }
                                      $isActive={availableYear === String(year)}
                                      onClick={() => {
                                        onYearChange(availableYear);
                                      }}
                                    >
                                      {availableYear}
                                    </YearSelectOption>
                                  ))}
                                </YearSelectList>
                              ) : null}
                            </YearSelectWrapper>
                          </DateControls>
                          <CurrencyTabsSlot>
                            <CurrencyYearArchiveCurrencyTabs
                              year={
                                displayedArchive ? displayedArchive.year : year
                              }
                              availableCurrencies={
                                displayedArchive?.availableCurrencies
                              }
                            />
                          </CurrencyTabsSlot>
                        </ControlsMain>
                        <SourceFilterSlot>
                          <Select
                            options={SOURCE_FILTER_OPTIONS}
                            value={selectedChartSource}
                            onChange={setSelectedChartSource}
                            titleExtractor={getSourceFilterMessageId}
                            labelExtractor={getSourceFilterMessageId}
                          />
                        </SourceFilterSlot>
                      </ControlsRow>
                    ) : null}
                  </HeaderBlock>
                  <CurrencyYearArchiveConverter
                    sourceType={selectedChartSource}
                    source={displayedArchive?.sources?.[selectedChartSource]}
                    selectedDate={
                      selectedArchiveMonth
                        ? selectedArchiveMonth.format('YYYY-MM-DD')
                        : null
                    }
                    currencyCode={currency}
                    currencyLabel={currencyLabel}
                  />
                </IntroBlock>
                <CurrencyYearArchiveChart
                  sourceType={selectedChartSource}
                  primarySource={
                    displayedArchive?.sources?.[selectedChartSource]
                  }
                  nbuSource={displayedArchive?.sources?.nbu}
                  isLoading={isArchiveLoading}
                />
              </HeaderSection>
            </CurrencyYearArchivePageContent>
            <PageSidebar />
          </PageInner>
          <CurrencyYearArchivePageFooter />
        </LayoutContainer>
      </Content>
    </Page>
  );
});

CurrencyYearArchivePage.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string,
    hash: PropTypes.string,
  }).isRequired,
  staticContext: PropTypes.objectOf(PropTypes.any),
};

CurrencyYearArchivePage.defaultProps = {
  staticContext: null,
};

export default withRouter(CurrencyYearArchivePage);
